import { DEFAULT_DATE_FORMAT } from '@/app-shell/constants';
import { useQueryUser } from '@heidi/workspace/data';
import { ADVANCED_EVIDENCE_IN_SESSION_QUOTA } from '@/hooks/useEvidenceInSessionLimits';
import { formatWithMomentToken } from '@/utils/dates';
import { endOfISOWeek, isValid, parseISO } from 'date-fns';
import { FormattedMessage } from 'react-intl';

export interface EvidenceQuotaConsumedToastBodyProps {
  /** advanced_used counter after the consume; used to compute remaining. */
  advancedUsed: number;
  /** UTC ISO-8601 reset boundary from the SSE chunk; falsy values fall back to end-of-week. */
  currentPeriodEnd?: string | null;
  /**
   * Whether the user's upgrade path is Practice rather than Clinician. Passed
   * in from the caller so the body stays a pure presentation component —
   * `useChatStream` resolves the live value from billing-info + reverse-trial
   * and threads it through `fireEvidenceQuotaConsumedToast`; the dev tool
   * passes an explicit override per chip.
   */
  isPracticeUpgradeTarget: boolean;
}

export type EvidenceQuotaConsumedToastResetCaptionProps = Pick<
  EvidenceQuotaConsumedToastBodyProps,
  'currentPeriodEnd'
>;

/**
 * Title body for the post-consume evidence quota System Toast (Figma nodes
 * `10492:57298` Clinician / `10742:53227` Practice). Renders the tier-aware
 * headline with pluralized credit count. The reset date is split off into
 * `EvidenceQuotaConsumedToastResetCaption` (rendered in the toast's `description`
 * slot at caption weight/size).
 */
export function EvidenceQuotaConsumedToastBody({
  advancedUsed,
  isPracticeUpgradeTarget,
}: Readonly<Omit<EvidenceQuotaConsumedToastBodyProps, 'currentPeriodEnd'>>) {
  const remaining = Math.max(0, ADVANCED_EVIDENCE_IN_SESSION_QUOTA - advancedUsed);

  if (isPracticeUpgradeTarget) {
    return (
      <FormattedMessage
        id="evQtTstP1"
        defaultMessage="{count, plural, =0 {You’ve used all your Practice credits this week.} one {You have # Practice credit remaining this week.} other {You have # Practice credits remaining this week.}}"
        description="Post-consume in-session evidence quota toast headline for Practice-tier users. The =0 (exhausted) branch swaps to 'You've used all...' copy per Figma `10492:57626` (mirrored for Practice); the one/other branches read out the remaining credits. The reset date renders separately in the toast description."
        values={{ count: remaining }}
      />
    );
  }

  return (
    <FormattedMessage
      id="evQtTstC1"
      defaultMessage="{count, plural, =0 {You’ve used all your Clinician credits this week.} one {You have # Clinician credit remaining this week.} other {You have # Clinician credits remaining this week.}}"
      description="Post-consume in-session evidence quota toast headline for Clinician-tier users. The =0 (exhausted) branch swaps to 'You've used all...' copy per Figma `10492:57626`; the one/other branches read out the remaining credits. The reset date renders separately in the toast description."
      values={{ count: remaining }}
    />
  );
}

/**
 * Reset-date caption rendered in the toast's `description` slot. Tier-agnostic
 * — the reset window is identical for Clinician and Practice quotas, so the
 * copy is shared. Owns the date-format lookup + end-of-ISO-week fallback so
 * the title stays a pure presentation component with no React Query hook.
 */
export function EvidenceQuotaConsumedToastResetCaption({
  currentPeriodEnd,
}: Readonly<EvidenceQuotaConsumedToastResetCaptionProps>) {
  const { data: dateFormat } = useQueryUser(
    (user) => user?.session_settings?.date_format ?? DEFAULT_DATE_FORMAT
  );

  // Fallback uses Sunday end-of-week (endOfISOWeek) to match the BE's
  // documented anchor for Evidence quota reset (see
  // evidence-quota-toast-dev-section.tsx; same fallback as
  // useEvidenceInSessionLimits.ts). A locale-defaulted endOfWeek would
  // land on Saturday in `en` — a 1-day drift from the BE anchor.
  // `isValid` guard absorbs malformed `currentPeriodEnd` payloads before
  // they reach `format`, which throws on Invalid Date.
  const parsedPeriodEnd = currentPeriodEnd ? parseISO(currentPeriodEnd) : null;
  const resolvedDateFormat = dateFormat ?? DEFAULT_DATE_FORMAT;
  const resetDate =
    parsedPeriodEnd && isValid(parsedPeriodEnd)
      ? formatWithMomentToken(parsedPeriodEnd, resolvedDateFormat)
      : formatWithMomentToken(endOfISOWeek(new Date()), resolvedDateFormat);

  return (
    <FormattedMessage
      id="evQtTstRst"
      defaultMessage="Resets {date}."
      description="Reset-date caption rendered under the post-consume evidence quota toast headline. Shared between Clinician and Practice tones — the quota reset window is identical for both."
      values={{ date: resetDate }}
    />
  );
}
